// The process-server portal (/?server=true): what another process server sees
// after the owner assigns them a job. Sign in with an emailed code; see ONLY
// their assigned jobs (person, address, access notes, papers); print the
// papers; log attempts with the same step-by-step screens, photo and GPS; and
// sign the Proof with their own name. Everything is enforced again on the
// server (netlify/functions/server-portal.js) — this page only asks.
import { useEffect, useRef, useState } from "react";
import AttemptWizard from "./AttemptWizard";
import { openInAppCamera } from "./InAppCamera";
import { exifFromFile } from "./lib/exif";
import { mapNoteFormToAttempt, outcomeLabel } from "./lib/serviceCaptureUi";
import { nowLocalDateTime, mapsUrl } from "./lib/fieldService";
import { isServedOutcome, diligenceDue } from "./lib/serviceState";
import { isBusiness, businessServiceMissing } from "./lib/attemptWording";
import { showNotice } from "./dialogs";

const API = "/.netlify/functions/server-portal";
async function call(payload) {
  const res = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok && data.ok !== false, status: res.status, data };
}
const newToken = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
// "Executed at" place for the Proof: the city of their business address.
const cityOf = (address) => { const parts = String(address || "").split(",").map(s => s.trim()).filter(Boolean); return parts.length >= 3 ? parts[parts.length - 2] : (parts[1] || ""); };
const BLANK = { status: "attempted", outcome: "", date: "", time: "", desc: "", gps: null, photos: [], photoStatement: "", features: [] };

export default function ServerApp({ tools }) {
  const T = tools || {};
  const [me, setMe] = useState(undefined);       // undefined = checking
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [jobs, setJobs] = useState([]);
  const [captureEnabled, setCaptureEnabled] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [logging, setLogging] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saveMsg, setSaveMsg] = useState("");
  const [docs, setDocs] = useState({});
  const [proofMsg, setProofMsg] = useState("");
  const saving = useRef(false);

  async function whoami() {
    const r = await call({ action: "whoami" });
    setMe(r.ok ? r.data : null);
    if (r.ok) loadJobs();
  }
  useEffect(() => { whoami(); }, []);
  async function loadJobs() {
    const r = await call({ action: "jobs" });
    if (r.ok) { setJobs(r.data.jobs || []); setCaptureEnabled(r.data.captureEnabled !== false); }
    else if (r.status === 401) setMe(null);
  }

  async function requestCode(e) {
    if (e) e.preventDefault();
    if (!email.trim()) { setMsg("Enter your email."); return; }
    setBusy(true); setMsg("");
    const r = await call({ action: "request_code", email: email.trim() });
    setBusy(false);
    if (!r.ok) { setMsg(r.data.error || "Could not send a code. Try again."); return; }
    setChallengeId(r.data.challengeId || "");
    setMsg(r.data.challengeId ? "Check your email for a 6-digit code." : (r.data.message || ""));
  }
  async function verifyCode(e) {
    if (e) e.preventDefault();
    setBusy(true); setMsg("");
    const r = await call({ action: "verify_code", challengeId, code: code.trim() });
    setBusy(false);
    if (!r.ok) { setMsg(r.data.error || "That code didn't work."); return; }
    setCode(""); setChallengeId(""); whoami();
  }
  async function logout() { await call({ action: "logout" }); setMe(null); setJobs([]); setOpenId(null); }

  const job = jobs.find(j => j.targetId === openId) || null;
  const served = job ? (["personal_service", "substituted_service", "posted_service"].includes(job.outcome) || (job.attempts || []).some(a => isServedOutcome(a.outcome))) : false;
  const jobCall = (payload) => call({ action: "job", payload: { jobNumber: job.jobNumber, targetId: job.targetId, ...payload } });
  async function loadDocs(j) {
    const r = await call({ action: "job", payload: { action: "list_docs", jobNumber: j.jobNumber, targetId: j.targetId } });
    if (r.ok) setDocs(d => ({ ...d, [j.targetId]: r.data.docs || [] }));
  }
  useEffect(() => { if (job) loadDocs(job); }, [openId]);

  // ── Log attempt (same step-by-step screens as the owner's app) ──────────
  function startLog() {
    setForm({ ...BLANK, ...nowLocalDateTime(), photos: (form.photos || []).filter(p => p && !p.stored) });
    setSaveMsg(""); setLogging(true);
    if (T.getCurrentGPS) T.getCurrentGPS().then(g => setForm(f => (f.gps ? f : { ...f, gps: g }))).catch(() => {});
  }
  function addPhotos(fileList, source) {
    Array.from(fileList || []).forEach(file => {
      const token = newToken();
      const addedAt = new Date().toISOString();
      const fileModifiedAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
      const exifP = exifFromFile(file);
      const reader = new FileReader();
      reader.onload = ev => {
        const original = ev.target.result;
        setForm(f => ({ ...f, photos: [...(f.photos || []), { token, dataUrl: original, filename: file.name, contentType: file.type, capturedAtDevice: addedAt, fileModifiedAt, source, gps: null, stored: false, stamping: true }] }));
        const finish = async (gNow) => {
          let stamped = null;
          const ex = await exifP;
          const g = ex.gps || gNow;
          // Taken with the portal's camera: date, time and GPS stamped on it.
          // Uploaded photos are never drawn on. The photo's own time/location win.
          if (source === "camera" && T.stampPhoto && T.photoStampLines) { try { stamped = await T.stampPhoto(original, T.photoStampLines(ex.takenAt || addedAt, g)); } catch (e) { stamped = null; } }
          setForm(f => ({ ...f, ...(ex.takenAt && !f.whenEdited ? nowLocalDateTime(new Date(ex.takenAt)) : {}),
            photos: (f.photos || []).map(p => (p.token === token ? { ...p, gps: g || null, gpsUnavailable: !g, stamping: false,
            ...(ex.takenAt ? { capturedAtDevice: ex.takenAt, timeFromPhoto: true } : {}),
            ...(stamped ? { dataUrl: stamped, contentType: "image/jpeg", filename: String(file.name || "photo").replace(/\.[^.]+$/, "") + "-stamped.jpg" } : {}) } : p)) }));
        };
        (T.getCurrentGPS ? T.getCurrentGPS() : Promise.reject()).then(g => finish(g)).catch(() => finish(null));
      };
      reader.readAsDataURL(file);
    });
  }
  const statementNeeded = (f) => { const ph = (f && f.photos) || []; return !!(f && f.outcome) && (ph.length === 0 || ph.some(p => p && p.gpsUnavailable && !p.gps)); };

  async function save() {
    const f = form;
    if (!f.date) { setSaveMsg("Enter the date."); return; }
    if (!f.outcome) { setSaveMsg("Finish the steps first."); return; }
    if ((f.photos || []).some(p => p && p.stamping)) { setSaveMsg("One moment — the photo is still being stamped with the time and GPS."); return; }
    // No photo / no reason is not a hard stop — the entry always saves.
    if (isBusiness(f.locationType) && f.status === "served" && f.serviceType !== "substitute") {
      const missing = businessServiceMissing(f);
      if (missing.length) { setSaveMsg(`Business service: enter ${missing.join(" and ")}.`); return; }
    }
    if (saving.current) return;
    saving.current = true; setSaveMsg("Saving…");
    try {
      let gps = f.gps;
      if (!gps && T.getCurrentGPS) { try { gps = await T.getCurrentGPS(); } catch (e) { /* optional */ } }
      const stmt = statementNeeded(f) ? String(f.photoStatement || "").trim() : "";
      const entry = { ...f, gps, desc: stmt ? [String(f.desc || "").trim(), `Photo/GPS statement (AB 747): ${stmt}`].filter(Boolean).join("\n") : f.desc };
      const attempt = mapNoteFormToAttempt(entry, job.jobNumber, newToken());
      attempt.target_id = job.targetId;
      const r = await call({ action: "attempt", attempt });
      if (!r.ok) { setSaveMsg(r.data.error || "The attempt was NOT saved. Please try again."); return; }
      const attemptId = r.data.attemptId || (r.data.attempt && r.data.attempt.id) || r.data.id;
      let failed = 0;
      for (const p of (f.photos || []).filter(x => x && x.dataUrl && !x.stored)) {
        const comma = String(p.dataUrl).indexOf(",");
        const up = await call({ action: "photo", payload: { action: "upload", jobNumber: job.jobNumber, attemptId, clientToken: p.token, filename: p.filename, contentType: p.contentType,
          capturedAtDevice: p.capturedAtDevice || null, fileModifiedAt: p.fileModifiedAt || null, source: p.source || null, gps: p.gps || gps || null,
          dataBase64: comma > -1 ? String(p.dataUrl).slice(comma + 1) : "" } });
        if (up.ok) setForm(x => ({ ...x, photos: (x.photos || []).map(q => (q.token === p.token ? { ...q, stored: true } : q)) }));
        else failed += 1;
      }
      setLogging(false);
      setForm(x => ({ ...BLANK, photos: (x.photos || []).filter(q => q && !q.stored) }));
      showNotice(failed ? `Saved, but ${failed} photo(s) did not upload — open Log Attempt to retry.` : (isServedOutcome(entry.outcome) ? "✓ Service saved." : "✓ Attempt saved."));
      await loadJobs();
      if (isServedOutcome(entry.outcome)) prepare("proof_of_service", attemptId);
    } catch (e) {
      setSaveMsg("Could not save (connection problem). Nothing was lost — tap Save again.");
    } finally { saving.current = false; }
  }

  // ── Job photos: no attempt needed (stamped with date, time and GPS) ─────
  const [jobPhotos, setJobPhotos] = useState({});
  const [photoBusy, setPhotoBusy] = useState(0);
  async function loadJobPhotos(j) {
    const r = await call({ action: "photo", payload: { action: "list_job", jobNumber: j.jobNumber, targetId: j.targetId } });
    if (r.ok) setJobPhotos(p => ({ ...p, [j.targetId]: r.data.photos || [] }));
  }
  useEffect(() => { if (job) loadJobPhotos(job); }, [openId]);
  function addJobPhotos(fileList, source) {
    const j = job;
    Array.from(fileList || []).forEach(file => {
      const token = newToken();
      const addedAt = new Date().toISOString();
      const fileModifiedAt = file.lastModified ? new Date(file.lastModified).toISOString() : null;
      setPhotoBusy(n => n + 1);
      const exifP = exifFromFile(file);
      const reader = new FileReader();
      reader.onload = async ev => {
        const original = ev.target.result;
        const ex = await exifP;
        let g = ex.gps || null;
        if (!g) { try { g = T.getCurrentGPS ? await T.getCurrentGPS() : null; } catch (e) { g = null; } }
        let dataUrl = original, contentType = file.type, filename = file.name;
        // Taken with the portal's camera: date, time and GPS stamped on it.
        if (source === "camera" && T.stampPhoto && T.photoStampLines) {
          try { dataUrl = await T.stampPhoto(original, T.photoStampLines(ex.takenAt || addedAt, g)); contentType = "image/jpeg"; filename = String(file.name || "photo").replace(/\.[^.]+$/, "") + "-stamped.jpg"; } catch (e) { dataUrl = original; }
        }
        const comma = String(dataUrl).indexOf(",");
        const r = await call({ action: "photo", payload: { action: "upload", jobNumber: j.jobNumber, targetId: j.targetId, clientToken: token, filename, contentType,
          capturedAtDevice: ex.takenAt || addedAt, fileModifiedAt, source, gps: g, dataBase64: comma > -1 ? String(dataUrl).slice(comma + 1) : "" } });
        setPhotoBusy(n => Math.max(0, n - 1));
        showNotice(r.ok ? "✓ Photo saved." : (r.data.error || "That photo could not be saved. Try again."));
        loadJobPhotos(j);
      };
      reader.readAsDataURL(file);
    });
  }
  async function viewJobPhoto(ph) {
    const r = await call({ action: "photo", payload: { action: "view", jobNumber: job.jobNumber, targetId: job.targetId, photoId: ph.id } });
    if (r.ok && r.data.url && T.openFileTab) T.openFileTab(r.data.url); else showNotice(r.data.error || "That photo could not be opened.");
  }

  // ── Proof: prepare → review → sign (with THEIR name) ─────────────────────
  async function prepare(kind, attemptId) {
    if (!job) return;
    setProofMsg(kind === "due_diligence" ? "Preparing the Declaration of Due Diligence…" : "Preparing the Proof of Service…");
    let classification = null;
    if (kind === "proof_of_service") {
      for (let pass = 0; pass < 6; pass += 1) {
        const c = await jobCall({ action: "classify_docs" });
        if (!c.ok) break;
        classification = c.data.classification || classification;
        if (!c.data.inProgress) break;
      }
    }
    const multi = !!(classification && classification.multipleProofsRequired && classification.separateProofs && classification.separateProofs.length > 1);
    const place = cityOf(me && me.address) || "California";
    const r = multi
      ? await jobCall({ action: "generate_proofs", executionDate: today(), executionPlace: place, attemptId: attemptId || undefined, requestId: `${job.targetId}:${Date.now()}` })
      : await jobCall({ action: "generate_doc", kind, executionDate: today(), executionPlace: place, attemptId: attemptId || undefined });
    setProofMsg(r.ok ? "Ready — review it, then tap Sign." : (r.data.error || "It could not be prepared. Tell the office."));
    await loadDocs(job);
  }
  async function sign(doc) {
    setProofMsg("Signing…");
    const opened = await jobCall({ action: "reopen_draft", docId: doc.id });
    if (!opened.ok) { setProofMsg(opened.data.error || "Could not open the draft."); return; }
    const name = (opened.data.values && opened.data.values.serverName) || (me && me.name) || "";
    if (!name) { setProofMsg("Your name is missing from your profile — ask the office to add it."); return; }
    const sigImage = T.scriptSignaturePng ? await T.scriptSignaturePng(name) : null;
    const r = await jobCall({ action: "sign_doc", kind: opened.data.kind || doc.kind, docId: opened.data.docId || doc.id, formCode: opened.data.formCode || doc.formCode || undefined,
      signatureName: name, signatureImage: sigImage, executionDate: today(), executionPlace: cityOf(me && me.address) || "California" });
    if (!r.ok) { setProofMsg((r.data.profileIncomplete ? "Your profile is missing something the Proof needs — ask the office to finish it. " : "") + (r.data.error || "Could not sign.")); return; }
    if ((opened.data.kind || doc.kind) === "proof_of_service") await jobCall({ action: "complete_job" });
    setProofMsg("✓ Signed. The office has it.");
    showNotice("✓ Signed.");
    await loadDocs(job); await loadJobs();
  }

  // ── Screens ─────────────────────────────────────────────────────────────
  const page = (children) => (<div className="section" style={{ maxWidth: 640, margin: "0 auto", padding: "1.25rem max(16px, env(safe-area-inset-right, 0px)) 2rem max(16px, env(safe-area-inset-left, 0px))" }}>{children}</div>);

  if (me === undefined) return page(<p style={{ textAlign: "center", color: "var(--gray)" }}>Loading…</p>);

  if (!me) return page(
    <form data-testid="server-login" onSubmit={challengeId ? verifyCode : requestCode} style={{ maxWidth: 340, margin: "1rem auto", textAlign: "center" }} autoComplete="on">
      <h2 className="section-title" style={{ fontSize: "1.5rem" }}>Process Server Login</h2>
      <p className="section-sub">Sign in to see the jobs assigned to you.</p>
      {!challengeId ? (<>
        <div className="field" style={{ textAlign: "left" }}><label htmlFor="server-email">Email</label>
          <input id="server-email" name="username" type="text" inputMode="email" autoCapitalize="none" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} /></div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ width: "100%", minHeight: 48 }}>{busy ? "Sending…" : "Email me a code"}</button>
      </>) : (<>
        <div className="field" style={{ textAlign: "left" }}><label htmlFor="server-code">6-digit code</label>
          <input id="server-code" name="one-time-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} autoFocus /></div>
        <button type="submit" className="btn-primary" disabled={busy || code.length < 6} style={{ width: "100%", minHeight: 48 }}>{busy ? "Checking…" : "Sign in"}</button>
        <button type="button" onClick={() => { setChallengeId(""); setCode(""); setMsg(""); }} style={{ marginTop: "0.75rem", background: "none", border: "none", color: "var(--navy)", textDecoration: "underline", cursor: "pointer" }}>Use a different email</button>
      </>)}
      {msg && <div className="alert" style={{ marginTop: "0.9rem", fontSize: "0.85rem" }}>{msg}</div>}
    </form>
  );

  if (job && logging) {
    const photosSlot = (
      <div className="quick-block">
        <button type="button" className="btn-primary" data-testid="server-take-photo" style={{ width: "100%", minHeight: 52, fontSize: "1rem", textTransform: "none", letterSpacing: 0 }}
          onClick={() => openInAppCamera(files => addPhotos(files, "camera"), "server-photo-camera")}>📷 Take photo of the door</button>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", marginTop: "0.4rem" }}>
          <span style={{ color: form.gps ? "var(--green)" : "var(--red)" }}>📍 {form.gps ? `${form.gps.lat.toFixed(5)}, ${form.gps.lng.toFixed(5)}` : "Getting location…"}</span>
          <button type="button" onClick={() => document.getElementById("server-photo-gallery").click()} style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.8rem" }}>From gallery</button>
        </div>
        <input id="server-photo-camera" type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { addPhotos(e.target.files, "camera"); e.target.value = ""; }} />
        <input id="server-photo-gallery" type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { addPhotos(e.target.files, "gallery"); e.target.value = ""; }} />
        <div className="photo-grid">
          {(form.photos || []).map(p => (
            <div key={p.token} style={{ position: "relative" }}>
              <img src={p.dataUrl} className="photo-thumb" alt="Evidence" />
              {!p.stored && <button type="button" aria-label="Remove photo" onClick={() => setForm(f => ({ ...f, photos: f.photos.filter(x => x.token !== p.token) }))}
                style={{ position: "absolute", top: 2, left: 2, width: 28, height: 28, borderRadius: 14, border: "none", background: "rgba(0,0,0,0.6)", color: "#fff" }}>✕</button>}
            </div>
          ))}
        </div>
      </div>
    );
    const statementSlot = statementNeeded(form) ? (
      <div className="serve-section" style={{ border: "2px solid #D97706", background: "#FEF9EE" }}>
        <h6>{(form.photos || []).length ? "No GPS on the photo — explain why" : "No photo — explain why"}</h6>
        <textarea value={form.photoStatement || ""} onChange={e => setForm(f => ({ ...f, photoStatement: e.target.value }))} style={{ minHeight: 180, width: "100%", fontSize: "1rem", boxSizing: "border-box" }}
          placeholder="e.g. Taking a photo was unsafe — dog loose in the yard." />
      </div>
    ) : null;
    return page(
      <AttemptWizard form={form} setForm={setForm} subjectName={job.subjectName} otherNames={[]} attempts={job.attempts} address={job.address}
        onCancel={() => setLogging(false)}
        slots={{
          photos: photosSlot, statement: statementSlot, diligence: null, mailing: null,
          footer: saveMsg ? <div className="alert alert-warn" style={{ marginTop: "0.75rem" }}>{saveMsg}</div> : null,
          save: <div className="mt1"><button className="btn-primary btn-log-attempt" data-testid="server-save-attempt" onClick={save} style={{ width: "100%", minHeight: 52 }}>Save Entry</button></div>,
        }} />
    );
  }

  if (job) {
    const jdocs = docs[job.targetId] || [];
    // Newest version per form; a draft already signed is not offered again.
    const latest = {};
    for (const d of jdocs) { const k = `${d.kind}|${d.formCode || ""}`; if (!latest[k] || (d.version || 0) > (latest[k].version || 0)) latest[k] = d; }
    const drafts = Object.values(latest).filter(d => d.status === "draft");
    const signed = Object.values(latest).filter(d => d.status === "signed");
    const dueDiligence = !served && diligenceDue(job.attempts || [], job.targetId);
    const map = mapsUrl(job.address, "", navigator.userAgent);
    return page(<>
      <button type="button" onClick={() => { setOpenId(null); setProofMsg(""); }} style={{ background: "none", border: "none", color: "var(--navy)", fontWeight: 700, cursor: "pointer", padding: "0.4rem 0", fontSize: "1rem" }}>‹ My jobs</button>
      <div className="serve-section" data-testid="server-job">
        <h3 style={{ fontFamily: "Montserrat, sans-serif", margin: "0 0 0.3rem" }}>{job.subjectName}</h3>
        <div>📍 {map ? <a href={map} target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>{job.address}</a> : (job.address || "No address")}</div>
        {job.neededBy && <div className="text-sm" style={{ marginTop: "0.3rem" }}>Needed by <strong>{job.neededBy}</strong></div>}
        {(job.gatedAccess || job.accessInstructions) && <div className="alert" style={{ marginTop: "0.5rem", fontSize: "0.85rem" }}>🔐 {job.accessInstructions || "Gated / access-controlled"}</div>}
        {job.notes && <div className="text-sm" style={{ marginTop: "0.4rem" }}>📝 {job.notes}</div>}
      </div>

      {captureEnabled && !served && (
        <button type="button" className="btn-primary btn-log-attempt" data-testid="server-log-attempt" onClick={startLog} style={{ width: "100%", minHeight: 52, margin: "0.5rem 0" }}>＋ Log Attempt / Service</button>
      )}

      <div className="serve-section" data-testid="server-job-photos">
        <h6>Photos</h6>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="btn-secondary" onClick={() => openInAppCamera(files => addJobPhotos(files, "camera"), "server-job-cam")}>📷 Take photo</button>
          <button type="button" className="btn-secondary" onClick={() => document.getElementById("server-job-gal").click()}>🖼️ Upload photos</button>
          {photoBusy > 0 && <span className="text-sm">Saving photo…</span>}
        </div>
        <input id="server-job-cam" type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => { addJobPhotos(e.target.files, "camera"); e.target.value = ""; }} />
        <input id="server-job-gal" type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { addJobPhotos(e.target.files, "gallery"); e.target.value = ""; }} />
        {(jobPhotos[job.targetId] || []).map(ph => (
          <div key={ph.id} className="photo-meta" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button type="button" onClick={() => viewJobPhoto(ph)} style={{ background: "none", border: "none", padding: 0, color: "var(--blue)", textDecoration: "underline", cursor: "pointer", fontSize: "0.75rem", minHeight: 32 }}>🖼️ View</button>
            <span>{ph.capturedAtDevice ? new Date(ph.capturedAtDevice).toLocaleString() : ""} · {ph.hasGps ? `${Number(ph.gpsLat).toFixed(5)}, ${Number(ph.gpsLng).toFixed(5)}` : "no GPS"}</span>
          </div>
        ))}
      </div>

      <div className="serve-section">
        <h6>Attempts</h6>
        {(job.attempts || []).length === 0 ? <p className="text-sm">No attempts yet.</p> : (job.attempts || []).map(a => (
          <div key={a.id} className="text-sm" style={{ padding: "0.3rem 0", borderBottom: "1px solid #eee" }}>
            <strong>{isServedOutcome(a.outcome) ? "✓ " : `#${a.attempt_number} `}{outcomeLabel(a.outcome)}</strong> · {a.attempt_date} {a.attempt_time ? String(a.attempt_time).slice(0, 5) : ""}
          </div>
        ))}
      </div>

      {(served || dueDiligence || jdocs.length > 0) && (
        <div className="serve-section" data-testid="server-proof">
          <h6>{served ? "Proof of Service" : "Declaration of Due Diligence"}</h6>
          {drafts.length === 0 && signed.length === 0 && (
            <button type="button" className="btn-primary" onClick={() => prepare(served ? "proof_of_service" : "due_diligence")} style={{ width: "100%", minHeight: 48 }}>Prepare {served ? "Proof" : "Declaration"}</button>
          )}
          {drafts.map(d => (
            <div key={d.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.5rem" }}>
              <span style={{ flex: "1 1 160px", minWidth: 0 }}>{d.formCode || d.kind} · draft</span>
              {d.url && <button type="button" className="btn-secondary" onClick={() => T.openFileTab && T.openFileTab(d.url)}>Review</button>}
              <button type="button" className="btn-primary" data-testid="server-sign" onClick={() => sign(d)}>✍️ Sign</button>
            </div>
          ))}
          {signed.map(d => (
            <div key={d.id} className="text-sm" style={{ marginTop: "0.5rem" }}>✓ Signed {d.formCode || d.kind} {d.url && <button type="button" className="btn-link" onClick={() => T.openFileTab && T.openFileTab(d.url)} style={{ background: "none", border: "none", color: "var(--blue)", textDecoration: "underline", cursor: "pointer" }}>Open</button>}</div>
          ))}
          {drafts.length > 0 && <button type="button" onClick={() => prepare(served ? "proof_of_service" : "due_diligence")} style={{ marginTop: "0.6rem", background: "none", border: "none", color: "var(--navy)", textDecoration: "underline", cursor: "pointer", fontSize: "0.85rem" }}>Prepare it again</button>}
          {proofMsg && <div className="alert" style={{ marginTop: "0.6rem", fontSize: "0.85rem" }}>{proofMsg}</div>}
        </div>
      )}

      <div className="serve-section">
        <h6>Papers to serve</h6>
        {(job.documents || []).length === 0 ? <p className="text-sm">No papers uploaded yet — ask the office.</p> : job.documents.map((d, i) => (
          <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.4rem" }}>
            <span style={{ flex: "1 1 160px", minWidth: 0, overflowWrap: "anywhere" }}>📄 {d.name}</span>
            <button type="button" className="btn-secondary" onClick={() => T.openFileTab && T.openFileTab(d.url)}>Open</button>
            <button type="button" className="btn-primary" onClick={() => T.printFile && T.printFile(d.url)}>🖨 Print</button>
          </div>
        ))}
      </div>
    </>);
  }

  return page(<>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
      <h2 className="section-title" style={{ fontSize: "1.4rem", margin: 0 }}>My Jobs</h2>
      <button type="button" className="btn-secondary" onClick={logout}>Log out</button>
    </div>
    <p className="section-sub" style={{ marginBottom: "0.75rem" }}>{me.name}</p>
    {jobs.length === 0 ? <p>No jobs assigned to you right now.</p> : jobs.map(j => (
      <button key={j.targetId} type="button" data-testid="server-job-card" onClick={() => { setOpenId(j.targetId); setProofMsg(""); window.scrollTo(0, 0); }}
        style={{ display: "block", width: "100%", textAlign: "left", background: "#fff", border: "1px solid #e5e7eb", borderLeft: `4px solid ${j.status === "completed" ? "var(--green)" : "var(--gold, #c8a24a)"}`, borderRadius: 8, padding: "0.8rem", marginBottom: "0.6rem", cursor: "pointer", fontFamily: "inherit" }}>
        <div style={{ fontWeight: 700, fontSize: "1rem" }}>{j.subjectName}</div>
        <div className="text-sm" style={{ marginTop: "0.2rem" }}>{j.address || "No address"}</div>
        <div className="text-sm" style={{ marginTop: "0.2rem", color: "var(--gray)" }}>
          {j.status === "completed" ? "✓ Done" : `${(j.attempts || []).length} attempt${(j.attempts || []).length === 1 ? "" : "s"}`}{j.neededBy ? ` · needed by ${j.neededBy}` : ""}
        </div>
      </button>
    ))}
  </>);
}
